"use client";

import { cn } from "@ui/lib";
import { Loader2Icon, MusicIcon, PlayIcon, XIcon } from "lucide-react";
import type {
	I2VReferenceMedia,
	I2VReferenceMediaKind,
} from "../hooks/use-image-to-video";

/** reference tab 文件框额外收的视频 / 音频类型，进来后一律在浏览器转成 mp4 / wav */
export const ACCEPTED_REFERENCE_MEDIA_TYPES = [
	"video/mp4",
	"video/quicktime",
	"video/webm",
	".mov",
	".m4v",
	"audio/mpeg",
	"audio/wav",
	"audio/x-wav",
	"audio/mp4",
	"audio/aac",
	"audio/ogg",
	".mp3",
	".wav",
	".m4a",
	".aac",
];

/**
 * 选进来的文件是视频、音频还是都不是（交给图片那条路）。不能只看 file.type：没装
 * QuickTime 的 Windows 上 .mov 的 type 是空串，会被当成图片拒掉
 */
export function getReferenceMediaKind(
	file: File,
): I2VReferenceMediaKind | null {
	if (
		file.type.startsWith("video/") ||
		/\.(mp4|mov|m4v|webm)$/i.test(file.name)
	) {
		return "video";
	}
	if (
		file.type.startsWith("audio/") ||
		/\.(mp3|wav|m4a|aac|ogg)$/i.test(file.name)
	) {
		return "audio";
	}
	return null;
}

/**
 * 参考视频 / 音频的缩略格。转码 / 上传中盖一层进度，完成后视频显示首帧、音频显示音符，
 * 左下角是时长。首页 hero 和 /image-to-video 共用，尺寸和圆角由 className 给。
 */
export function ReferenceMediaThumb({
	media,
	processingLabel,
	removeLabel,
	onRemove,
	className,
}: {
	media: I2VReferenceMedia;
	/** 例如「处理中 45%」，调用方按 media.progress 拼好 */
	processingLabel: string;
	removeLabel: string;
	onRemove: () => void;
	className?: string;
}) {
	const ready = Boolean(media.url && media.durationSec);
	return (
		<div
			className={cn(
				"relative shrink-0 overflow-hidden bg-gray-2 ring-1 ring-black/[.04]",
				className,
			)}
		>
			{media.kind === "video" && media.previewUrl ? (
				// #t=0.1：iOS Safari 不带时间片段不画首帧
				<video
					src={`${media.previewUrl}#t=0.1`}
					muted
					playsInline
					preload="metadata"
					className="size-full object-cover"
				/>
			) : null}
			{media.kind === "audio" ? (
				<span className="flex size-full items-center justify-center bg-brand-a08 text-brand">
					<MusicIcon className="size-6" />
				</span>
			) : null}
			{ready ? (
				<span className="absolute bottom-1 left-1 flex items-center gap-0.5 rounded-full bg-black/60 px-1.5 py-0.5 font-medium text-[10px] text-white tabular-nums">
					{media.kind === "video" ? (
						<PlayIcon className="size-2.5 fill-current" />
					) : (
						<MusicIcon className="size-2.5" />
					)}
					{`${Math.round(media.durationSec)}s`}
				</span>
			) : (
				<span className="absolute inset-0 flex flex-col items-center justify-center gap-1 bg-black/50 px-1 text-center text-[11px] text-white">
					<Loader2Icon className="size-4 animate-spin" />
					{processingLabel}
				</span>
			)}
			<button
				type="button"
				onClick={onRemove}
				aria-label={removeLabel}
				className="absolute top-1.5 right-1.5 flex size-6 items-center justify-center rounded-full bg-black/60 text-white hover:bg-black/80"
			>
				<XIcon className="size-3.5" />
			</button>
		</div>
	);
}
